import { Component } from "react"
import PropTypes from "prop-types"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

import {
    createOrderByFilterGroup,
    genericFilterGroup,
    FromTrackingBoardInvisibleFilterGroup,
} from "shared/search/containers/FilterGroup/FilterGroups"

import {
    createListFilter,
    createNotInListFilter,
    ManuallyChangedInListFilter,
    PriorityFilter,
    PriorityLastUserFilter,
    StanceFilter,
    StanceLastUserFilter,
    ReviewedByMeFilter,
    ReviewedByOrgFilter,
    ReviewedByTeamFilter,
    ReviewedByUserFilter,
    NotReviewedByUserFilter,
} from "shared/search/containers/Filter/Filters"
import {
    ArticleAuthorFilter,
    ArticleCountryFilter,
    ArticleCountryEUFilter,
    ArticleOfficialsMentionFilter,
    ArticleLanguageFilter,
    ArticleOutletFilter,
    ArticleOutletCoverageFilter,
    ArticleOutletTypeFilter,
    ArticlePressFilter,
    ArticleProjectFilter,
    ArticlePublishDateListNoFutureFilter,
    ArticlePublishDatePickerEndNoFutureFilter,
    ArticlePublishDatePickerStartNoFutureFilter,
    ArticleSourceTypeFilter,
    ArticleSourceRankFilter,
    ArticleSourceNameFilter,
    ArticleSourceIndustryFilter,
    ArticleStateFilter,
    ArticleTitleFilter,
    ArticleTopicFilter,
    ArticleTopicLevelFilter,
    ArticleTrackingDashboardFilter,
    showSyndicatedArticlesFilter,
} from "shared/search/containers/Filter/ArticleFilters"

export class ArticleTypeFilterGroup extends Component {
    static defaultProps = {
        filters: [ArticleSourceTypeFilter],
        uniqueFilterGroupId: "articleTypeFilterGroup",
        displayLabel: "Article Type",
        helpText: "Filters relating to source type.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleTopicFilterGroup extends Component {
    static defaultProps = {
        filters: [ArticleTopicFilter],
        uniqueFilterGroupId: "articleTopicFilterGroup",
        displayLabel: "Topic",
        helpText: "Filters relating to source topic.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleSourceFilterGroup extends Component {
    static defaultProps = {
        filters: [
            ...(Userdata.Permissions.org_media_relations ? [ArticleOutletFilter] : []),
            ArticleSourceNameFilter,
            ArticleSourceIndustryFilter,
            ArticleOutletTypeFilter,
            ArticleOutletCoverageFilter,
            ...(Userdata.Permissions.org_media_relations ? [ArticlePressFilter] : []),
            ArticleAuthorFilter,
            ArticleSourceRankFilter,
        ],
        uniqueFilterGroupId: "articleSourceFilterGroup",
        displayLabel: "Source",
        helpText: "Filters relating to a specific source.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

const getArticleRegionFilters = () => {
    // when in Internationa, Global, or a custom region contains international/eu regions
    // single international region is already excluded at the ArticleFilterSet level
    if (Userdata.isGlobalMode() || Userdata.hasInternationalMode()) {
        return [ArticleCountryFilter]
        // when in EU region
    } else if (Userdata.isOnlyEUMode()) {
        return [ArticleCountryEUFilter]
    }
    return [ArticleStateFilter]
}
export class ArticleRegionFilterGroup extends Component {
    static defaultProps = {
        filters: getArticleRegionFilters(),
        uniqueFilterGroupId: "articleRegionFilterGroup",
        displayLabel: "Region",
        helpText: "Filters relating to geography.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleTitleFilterGroup extends Component {
    static defaultProps = {
        filters: [ArticleTitleFilter],
        uniqueFilterGroupId: "articleTitleFilterGroup",
        displayLabel: "Headline",
        helpText: "Filter by article title.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleDateFilterGroup extends Component {
    static defaultProps = {
        filters: [
            ArticlePublishDateListNoFutureFilter,
            ArticlePublishDatePickerStartNoFutureFilter,
            ArticlePublishDatePickerEndNoFutureFilter,
        ],
        uniqueFilterGroupId: "articleDateFilterGroup",
        displayLabel: "Date",
        helpText: "Filter for results within a specific date range.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleLanguageFilterGroup extends Component {
    static defaultProps = {
        filters: [ArticleLanguageFilter],
        uniqueFilterGroupId: "articleLanguageFilterGroup",
        displayLabel: "Language",
        helpText: "Filter for results with a specific language.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

/* -------------
* Tracking Tool Filters
------------- */

export class ArticleMentionFilterGroup extends Component {
    static defaultProps = {
        filters: [ArticleOfficialsMentionFilter],
        uniqueFilterGroupId: "articleMentionFilterGroup",
        displayLabel: "Referencing",
        helpText:
            "Filter articles that reference a specific entity." +
            "We use natural language processing to automatically form these relationships.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleStanceFilterGroup extends Component {
    static defaultProps = {
        filters: [StanceFilter, StanceLastUserFilter],
        uniqueFilterGroupId: "articleStanceFilterGroup",
        displayLabel: "Organization Stance",
        helpText: "Filter articles by your organization's stance.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleProjectFilterGroup extends Component {
    static defaultProps = {
        filters: [ArticleProjectFilter],
        uniqueFilterGroupId: "articleProjectFilterGroup",
        displayLabel: "Issues",
        helpText: "Filter articles by issue assigned.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticleListFilterGroup extends Component {
    static defaultProps = {
        filters: [
            createListFilter(DjangIO.app.models.QuorumDataType.media.value),
            createNotInListFilter(DjangIO.app.models.QuorumDataType.media.value),
            ManuallyChangedInListFilter,
        ],
        uniqueFilterGroupId: "articleListFilterGroup",
        displayLabel: "Lists",
        helpText: "Filter article by lists of articles that you've created " + "and saved to Projects.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class ArticlePriorityFilterGroup extends Component {
    static defaultProps = {
        filters: [PriorityFilter, PriorityLastUserFilter],
        uniqueFilterGroupId: "articlePriorityFilterGroup",
        displayLabel: "Priority",
        helpText: "Filter articles by your organization's indicated priority.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

const defaultProps = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}

export const ArticleTrackingDashboardFilterGroup = (props) => genericFilterGroup(props)
ArticleTrackingDashboardFilterGroup.propTypes = defaultProps
ArticleTrackingDashboardFilterGroup.defaultProps = {
    filters: [ArticleTrackingDashboardFilter],
    uniqueFilterGroupId: "articleTrackingDashboardFilterGroup",
    displayLabel: "Tracking Board",
    helpText: "Filter for results that currently appear on the unreviewed news articles tab in your tracking board(s).",
}

export const ArticleSyndicationFilterGroup = (props) => genericFilterGroup(props)
ArticleSyndicationFilterGroup.propTypes = defaultProps
ArticleSyndicationFilterGroup.defaultProps = {
    filters: [showSyndicatedArticlesFilter],
    uniqueFilterGroupId: "articleSyndicationFilterGroup",
    displayLabel: "Syndication",
    helpText: "Show or do not show syndicated (duplicated) articles.",
}

export const ArticleTopicLevelFilterGroup = (props) => genericFilterGroup(props)
ArticleTopicLevelFilterGroup.propTypes = defaultProps
ArticleTopicLevelFilterGroup.defaultProps = {
    filters: [ArticleTopicLevelFilter],
    uniqueFilterGroupId: "articleTopicLevelFilterGroup",
    displayLabel: "Topic Type",
    helpText: "Filter topics by their type.",
}

export const ArticleOrderByFilterGroup = createOrderByFilterGroup({
    // uniqueFilterId = "mediaOrderBy"
    quorumDataType: DjangIO.app.models.QuorumDataType.media.value,
})

export const ArticleReviewFilterGroup = (props) => genericFilterGroup(props)
ArticleReviewFilterGroup.propTypes = defaultProps
ArticleReviewFilterGroup.defaultProps = {
    filters: [
        ReviewedByMeFilter,
        ReviewedByOrgFilter,
        ReviewedByUserFilter,
        ReviewedByTeamFilter,
        NotReviewedByUserFilter,
    ],
    uniqueFilterGroupId: "articleReviewFilterGroup",
    displayLabel: "Review Status",
    helpText: "Filter results by review status of article",
}

export const ArticleFilterGroups = [
    ArticleTypeFilterGroup,
    ArticleSourceFilterGroup,
    // hide region filtergroup when in single international/eu region
    ...(Userdata.current_regions_values.length === 1 && Userdata.isOnlyIntlegMode() ? [] : [ArticleRegionFilterGroup]),
    ArticleTitleFilterGroup,
    ArticleSyndicationFilterGroup,
    ArticleDateFilterGroup,
    ArticleLanguageFilterGroup,
    ArticleMentionFilterGroup,
    ArticleTopicFilterGroup,
    ArticleListFilterGroup,
    ArticleProjectFilterGroup,
    ArticleStanceFilterGroup,
    ArticlePriorityFilterGroup,
    // AssignmentFilterGroup, // TODO: Reinstate at Product's discretion.
    ArticleTrackingDashboardFilterGroup,
    ArticleOrderByFilterGroup,
    ArticleReviewFilterGroup,
    FromTrackingBoardInvisibleFilterGroup,
]
